"use client";

import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import ScrollReveal from "@/components/common/ScrollReveal";
import MobileAppPhoneArt from "./MobileAppPhoneArt";

// Icon positions (in the 370x502 composition coordinate system from Figma node 694:71324)
// Each icon is a solid black ring with a pictogram inside + dashed circle overlay
const iconRings = [
  // Top icon (lock with chain) - Group 3 Copy 2 (86x86) + Group 13 Copy (107x108) + Group 6 pictogram
  {
    ringLeft: 148,
    ringTop: 10.48,
    ringSize: 86,
    innerLeft: 139.71,
    innerTop: 0,
    innerWidth: 107.25,
    innerHeight: 108.2,
    innerRotate: 87,
    iconFile: "mobile-pentest-icon1.svg", // lock/chain icon
    iconLeft: 171,
    iconTop: 21.48,
    iconWidth: 39.35,
    iconHeight: 62.84,
  },
  // Upper-right icon (shield) - Group 3 Copy (98x98) + Group 13 (111x110) + Group 15 pictogram
  {
    ringLeft: 247,
    ringTop: 102.48,
    ringSize: 98,
    innerLeft: 243,
    innerTop: 94.48,
    innerWidth: 111,
    innerHeight: 110,
    innerRotate: 0,
    iconFile: "mobile-pentest-icon2.svg", // shield icon
    iconLeft: 265,
    iconTop: 123.48,
    iconWidth: 62,
    iconHeight: 57,
  },
  // Lower-right icon (cube/box) - Group 3 Copy 4 (102x102) + Group 13 Copy 3 (119x118) + Group 48 pictogram
  {
    ringLeft: 258,
    ringTop: 252.48,
    ringSize: 102,
    innerLeft: 251,
    innerTop: 241.48,
    innerWidth: 119,
    innerHeight: 118,
    innerRotate: 0,
    iconFile: "mobile-pentest-icon3.svg",
    iconLeft: 274,
    iconTop: 277.48,
    iconWidth: 69,
    iconHeight: 52,
  },
  // Bottom icon (envelope) - Group 3 (129x129) + Group 13 Copy 4 (140x139) + Group 25 pictogram
  {
    ringLeft: 146,
    ringTop: 372.48,
    ringSize: 129,
    innerLeft: 146,
    innerTop: 363.48,
    innerWidth: 140,
    innerHeight: 139,
    innerRotate: 0,
    iconFile: "mobile-pentest-icon4.svg",
    iconLeft: 174,
    iconTop: 395.93,
    iconWidth: 72.83,
    iconHeight: 63.55,
  },
];

export default function MobileApplicationPenetrationTest() {
  // Staggered icon entrance, triggered when the illustration scrolls into view
  // (ports v1's per-icon fadeIn with 0.2s-0.8s staggered delays).
  const illoRef = useRef<HTMLDivElement>(null);
  const [iconsIn, setIconsIn] = useState(false);

  useEffect(() => {
    const el = illoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIconsIn(true);
          observer.disconnect();
        }
      },
      { threshold: 0.25 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="relative w-full px-4 sm:px-6 lg:px-8 pb-16">
      {/* Same top alignment and pt-2 as the other two Assessment tabs, so the
          copy does not jump when you switch between Web / Mobile / Compromise:
          the layout already puts 48px under the tab row. */}
      <div className="relative z-10 mx-auto flex w-full max-w-[1300px] flex-col items-start gap-10 pt-2 lg:flex-row lg:items-start lg:justify-between lg:gap-[120px]">
        {/* Left: Body copy */}
        <div className="flex w-full max-w-[640px] flex-col gap-6 sm:gap-7 text-text-highlight">
          <ScrollReveal direction="up" delay={0}>
            <p className="font-[var(--font-avenir)] text-[16px] leading-[28px] tracking-[0.5px] text-white">
              According to Statista, 3.8 billion people in the world today are
              smartphone users and they spend almost 90% of their time on apps.
              The mobile app industry is thriving and expected to generate over
              $935 billion dollars by 2023. The unstoppable growth of this
              industry also brings a lot of security related issues due to the
              rush release of mobile app without adequate security testing and
              protection.
            </p>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={80}>
            <p className="font-[var(--font-avenir)] text-[16px] leading-[28px] tracking-[0.5px] text-white">
              ECQ offers Mobile App Security Assessment service to test mobile
              app and its architecture for potential security vulnerabilities.
              The Consultants also develop Proof-of-Concept or exploit to
              validate high risk vulnerabilities and help an organization to
              prioritize remediation efforts.
            </p>
          </ScrollReveal>

          <ScrollReveal direction="up" delay={160}>
            <p className="font-[var(--font-avenir)] text-[16px] leading-[28px] tracking-[0.5px] text-white">
              ECQ follows its in-house RAPID penetration test framework
              combined with OWASP Mobile Top 10, and OWASP Mobile Security
              Testing Guide (MSTG) to carry out all of the assessment
              activities.
            </p>
          </ScrollReveal>
        </div>

        {/* Right: Composition. The artwork is portrait (370×502), so letting it
            take the full content width on a phone makes it as tall as the copy
            above it — cap it below `lg` and grow back to the Figma size on
            desktop. */}
        <div className="relative flex w-full max-w-[260px] sm:max-w-[300px] md:max-w-[330px] lg:max-w-[370px] flex-shrink-0 items-center justify-center self-center">
          <ScrollReveal direction="up" delay={120} className="w-full">
            <div
              ref={illoRef}
              className="relative mx-auto w-full"
              style={{ aspectRatio: "370 / 502" }}
            >
              {/* Phone illustration (left side of composition). Inlined so its
                  frames, message pills, data squares and fingerprint badge can
                  animate layer by layer. */}
              <div
                className="absolute"
                style={{
                  left: `${(0 / 370) * 100}%`,
                  top: `${(45.48 / 502) * 100}%`,
                  width: `${(211 / 370) * 100}%`,
                  height: `${(327 / 502) * 100}%`,
                }}
              >
                <MobileAppPhoneArt playing={iconsIn} className="h-full w-full" />
              </div>

              {/* 4 circular icons on the right side. Each arrives after the
                  phone has drawn itself: a scale-up with a small overshoot, one
                  icon at a time, then a slow float. Hover scales the icon up. */}
              {iconRings.map((ring, idx) => (
                <div
                  key={idx}
                  className="absolute"
                  style={{
                    left: `${(ring.innerLeft / 370) * 100}%`,
                    top: `${(ring.innerTop / 502) * 100}%`,
                    width: `${(ring.innerWidth / 370) * 100}%`,
                    height: `${(ring.innerHeight / 502) * 100}%`,
                  }}
                >
                  {/* Entrance layer */}
                  <div
                    className={`h-full w-full ${iconsIn ? "mapt-pop" : "mapt-hidden"}`}
                    style={{ animationDelay: `${1500 + idx * 170}ms` }}
                  >
                    {/* Ambient float layer (staggered so icons don't move in unison) */}
                    <div
                      className="mapt-float h-full w-full"
                      style={{ animationDelay: `${idx * 0.9}s` }}
                    >
                      {/* Hover layer. No orbiting marker here on purpose: the
                          icon assets already carry dots on their own ring, and
                          an overlay circle cannot follow it — the wrapper box is
                          not square and the art sits inside it via
                          object-contain, so the overlay traces an ellipse that
                          misses the ring. */}
                      <div className="relative h-full w-full transition-transform duration-300 ease-out hover:scale-[1.12]">
                        <Image
                          src={`/images/service/offend/mobile-application-penetration-test/${ring.iconFile}`}
                          alt={`Icon ${idx + 1}`}
                          fill
                          className="object-contain"
                          style={
                            ring.innerRotate
                              ? { transform: `rotate(${ring.innerRotate}deg)` }
                              : undefined
                          }
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </div>

      {/* Icon animations. The float loop is the v1 SPIN_1 idle motion
          (mobile-app-penetration-test.html #group-bug); the entrance and the
          orbiting dot are new, timed to land after the phone has drawn itself
          in MobileAppPhoneArt. */}
      <style jsx>{`
        /* Before the section is in view, the icons are simply not there yet. */
        .mapt-hidden {
          opacity: 0;
        }
        .mapt-pop {
          animation: maptPop 620ms cubic-bezier(0.2, 0.75, 0.3, 1) both;
          will-change: transform, opacity;
        }
        .mapt-float {
          animation: maptFloat 6s ease-in-out infinite;
          will-change: transform;
        }
        @keyframes maptPop {
          0% {
            opacity: 0;
            transform: scale(0.55);
          }
          65% {
            opacity: 1;
            transform: scale(1.06);
          }
          100% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes maptFloat {
          0% {
            transform: translate(5px, -3px);
          }
          25% {
            transform: translate(0px, -5px);
          }
          50% {
            transform: translate(-5px, 0px);
          }
          75% {
            transform: translate(0px, 5px);
          }
          100% {
            transform: translate(5px, -3px);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .mapt-hidden {
            opacity: 1;
          }
          .mapt-pop,
          .mapt-float {
            animation: none !important;
          }
        }
      `}</style>
    </section>
  );
}
